import React, { FC, useEffect, useState } from "react";
import {
  Drawer,
  Button,
  Form,
  Input,
  Select,
  Space,
  Row,
  Col,
  message,
  Alert,
  Popover,
} from "antd";
import { PlusOutlined, MinusCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";

const { Option } = Select;

interface MaterialItem {
  key: string;
  materialName: string;
  tenderMaterialId?: number;
  askedQty: number;
  remainingQty: number;
}

interface SupplierData {
  supplierID: string;
  supplierName: string;
  lineItems: {
    materialName: string;
    quotedQuantity: number;
    quotedUnitPrice: number;
  }[];
}

interface SubmittedDataItem {
  materialName: string;
  tenderMaterialId?: number;
  supplierID: string;
  supplierName: string;
  awardQty: number;
  reason: string;
}

interface TenderAllocationDrawerProps {
  visible: boolean;
  onClose: () => void;
  materials: MaterialItem[];
  suppliers: SupplierData[];
  onSubmit: (values: SubmittedDataItem[]) => void;
}

const TenderAllocationDrawer: FC<TenderAllocationDrawerProps> = ({
  visible,
  onClose,
  materials,
  suppliers,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const [showError, setShowError] = useState(false);
  const [remainingQty, setRemainingQty] = useState<Record<string, number>>({});
  const [selectedSupplierInfo, setSelectedSupplierInfo] = useState<
    Record<string, any>
  >({});

  useEffect(() => {
    if (visible) {
      const initialRemaining: Record<string, number> = {};
      materials.forEach((m) => {
        initialRemaining[m.key] = m.remainingQty;
      });
      setRemainingQty(initialRemaining);
      setShowError(false);
      setSelectedSupplierInfo({});
      form.resetFields();
      // Set default form values
      const defaults: any = {};
      materials.forEach((m) => {
        defaults[`material_${m.key}`] = [
          { supplier: undefined, awardQty: "", reason: "" },
        ];
      });
      form.setFieldsValue(defaults);
    }
  }, [visible, materials, form]);

  const getSuppliersForMaterial = (material: MaterialItem) => {
    if (material.tenderMaterialId != null) {
      return suppliers.filter((s) =>
        (s.lineItems || []).some((li: any) => li.tenderMaterialId === material.tenderMaterialId)
      );
    }
    return suppliers.filter((s) =>
      (s.lineItems || []).some((li: any) => li.materialName === material.materialName)
    );
  };

  const getSupplierQuotationInfo = (
    supplierID: string,
    material: MaterialItem
  ) => {
    const supplier = suppliers.find((s) => s.supplierID === supplierID);
    const lineItem = (supplier?.lineItems || []).find((li: any) =>
      material.tenderMaterialId != null
        ? li.tenderMaterialId === material.tenderMaterialId
        : li.materialName === material.materialName
    );
    return lineItem || null;
  };

  const handleSupplierChange = (
    supplierID: string,
    materialKey: string,
    material: MaterialItem,
    fieldIndex: number
  ) => {
    const info = getSupplierQuotationInfo(supplierID, material);
    const key = `${materialKey}_${fieldIndex}`;
    setSelectedSupplierInfo((prev) => ({
      ...prev,
      [key]: info,
    }));
  };

  const handleAwardQtyBlur = (material: MaterialItem) => {
    // Recalculate remaining qty for this material
    const formValues = form.getFieldsValue();
    let totalAwarded = 0;
    const entries = formValues[`material_${material.key}`] || [];
    entries.forEach((entry: any) => {
      if (entry?.awardQty) {
        totalAwarded += Number(entry.awardQty);
      }
    });
    setRemainingQty((prev) => ({
      ...prev,
      [material.key]: Math.max(material.remainingQty - totalAwarded, 0),
    }));
  };

  const handleFormSubmit = (values: any) => {
    const submittedData: SubmittedDataItem[] = [];
    let hasAllocation = false;

    materials.forEach((material) => {
      const materialData = values[`material_${material.key}`];
      if (Array.isArray(materialData)) {
        materialData.forEach((entry: any) => {
          if (entry?.supplier && entry?.awardQty) {
            hasAllocation = true;
            const supplier = suppliers.find(
              (s) => s.supplierID === entry.supplier
            );
            submittedData.push({
              materialName: material.materialName,
              tenderMaterialId: material.tenderMaterialId,
              supplierID: entry.supplier,
              supplierName: supplier?.supplierName || "",
              awardQty: Number(entry.awardQty),
              reason: entry.reason || "",
            });
          }
        });
      }
    });

    if (!hasAllocation) {
      setShowError(true);
      return;
    }

    onSubmit(submittedData);
    form.resetFields();
    onClose();
  };

  return (
    <Drawer
      title="Allocate / Award"
      width="70vw"
      onClose={onClose}
      open={visible}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button onClick={() => form.submit()} type="primary">
            Submit
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        onFinish={handleFormSubmit}
      >
        {showError && (
          <>
            <Alert
              message="Please allocate at least one material."
              type="error"
              showIcon
              closable
              onClose={() => setShowError(false)}
            />
            <br />
          </>
        )}

        {materials.map((material) => (
          <div
            key={material.key}
            style={{
              marginBottom: 24,
              padding: 16,
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <Row gutter={16} className="mb-3">
              <Col span={12}>
                <h6 style={{ margin: 0 }}>Material: {material.materialName}</h6>
              </Col>
              <Col span={12} style={{ textAlign: "right" }}>
                <span>
                  Asked Qty: <b>{material.askedQty}</b> &nbsp;|&nbsp; Remaining
                  Qty:{" "}
                  <b>
                    {remainingQty[material.key] !== undefined
                      ? remainingQty[material.key]
                      : material.remainingQty}
                  </b>
                </span>
              </Col>
            </Row>

            <Form.List name={`material_${material.key}`}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }, fieldIndex) => {
                    const infoKey = `${material.key}_${fieldIndex}`;
                    const supplierInfo = selectedSupplierInfo[infoKey];

                    return (
                      <Space
                        key={key}
                        style={{
                          display: "flex",
                          marginBottom: 8,
                          alignItems: "flex-start",
                        }}
                      >
                        <Form.Item
                          {...restField}
                          name={[name, "supplier"]}
                          label="Supplier"
                          rules={[
                            {
                              required: true,
                              message: "Select a supplier",
                            },
                          ]}
                          style={{ width: 200 }}
                        >
                          <Select
                            placeholder="Select Supplier"
                            allowClear
                            onChange={(val) =>
                              handleSupplierChange(
                                val,
                                material.key,
                                material,
                                fieldIndex
                              )
                            }
                          >
                            {getSuppliersForMaterial(material).map(
                              (s) => (
                                <Option key={s.supplierID} value={s.supplierID}>
                                  {s.supplierName}
                                </Option>
                              )
                            )}
                          </Select>
                        </Form.Item>

                        <Form.Item
                          label={
                            <span>
                              Unit Price{" "}
                              {supplierInfo && (
                                <Popover
                                  content={
                                    <div>
                                      <div>
                                        <b>Supply Qty:</b>{" "}
                                        {supplierInfo.quotedQuantity}
                                      </div>
                                      <div>
                                        <b>Unit Price:</b> INR{" "}
                                        {supplierInfo.quotedUnitPrice.toLocaleString(
                                          "en-IN",
                                          { minimumFractionDigits: 2 }
                                        )}
                                      </div>
                                      <div>
                                        <b>Total Price:</b> INR{" "}
                                        {(
                                          supplierInfo.quotedQuantity *
                                          supplierInfo.quotedUnitPrice
                                        ).toLocaleString("en-IN", {
                                          minimumFractionDigits: 2,
                                        })}
                                      </div>
                                    </div>
                                  }
                                  title="Quotation Details"
                                >
                                  <InfoCircleOutlined
                                    style={{ marginLeft: 4, color: "#1890ff" }}
                                  />
                                </Popover>
                              )}
                            </span>
                          }
                          style={{ width: 160 }}
                        >
                          <div
                            style={{
                              background: "#f5f5f5",
                              borderRadius: 6,
                              padding: "5px 12px",
                              height: 32,
                              lineHeight: "22px",
                              fontSize: 13,
                            }}
                          >
                            {supplierInfo
                              ? `INR ${supplierInfo.quotedUnitPrice.toLocaleString(
                                  "en-IN",
                                  { minimumFractionDigits: 2 }
                                )}`
                              : "N/A"}
                          </div>
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "awardQty"]}
                          label="Award Qty"
                          rules={[
                            {
                              required: true,
                              message: "Enter award qty",
                            },
                            () => ({
                              validator(_, value) {
                                if (value && Number(value) <= 0) {
                                  return Promise.reject(
                                    "Must be greater than 0"
                                  );
                                }
                                return Promise.resolve();
                              },
                            }),
                          ]}
                          style={{ width: 140 }}
                        >
                          <Input
                            placeholder="Award Qty"
                            type="number"
                            onBlur={() =>
                              handleAwardQtyBlur(material)
                            }
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          name={[name, "reason"]}
                          label="Reason for Award"
                          rules={[
                            {
                              required: true,
                              message: "Enter reason",
                            },
                          ]}
                          style={{ width: 200 }}
                        >
                          <Input placeholder="Reason for Award" />
                        </Form.Item>

                        {fields.length > 1 && (
                          <MinusCircleOutlined
                            style={{ marginTop: 38, color: "#ff4d4f" }}
                            onClick={() => {
                              remove(name);
                              setTimeout(
                                () => handleAwardQtyBlur(material),
                                100
                              );
                            }}
                          />
                        )}
                      </Space>
                    );
                  })}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      disabled={remainingQty[material.key] === 0}
                    >
                      Add Supplier
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </div>
        ))}
      </Form>
    </Drawer>
  );
};

export default TenderAllocationDrawer;
